import { useRouter } from '@/i18n/navigation';
import { auth } from '@/lib/firebase';
import { HttpResponseError } from '@/lib/http-error';
import * as UserFactory from '@/services/user';
import { useFeatureFlags } from '@/store/feature-flag.store';
import { FirebaseError } from 'firebase/app';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { useFormik } from 'formik';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { LoginFormValidation } from './formValidation';
import { setTokenClient } from '@/lib/auth-token';

const useLoginFormViewModel = () => {
  const { flags } = useFeatureFlags();
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean | null>(null);
  const [errorType, setErrorType] = useState<string | null>(null);
  const router = useRouter();
  const translation = useTranslations('user_not_found');
  const isDevelopmentEnvironment = process.env.NODE_ENV === 'development';
  const isFirebaseAuthEmulatorEnabled =
    !!process.env.NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_URL;

  const signIn = async (values: { email: string; password: string }) => {
    if (isDevelopmentEnvironment && isFirebaseAuthEmulatorEnabled) {
      await getAndStoreEmulatorToken(values);
      return;
    }

    if (isDevelopmentEnvironment) {
      await getAndStoreFirebaseToken(values);
      return;
    }
    await getAndStoreSessionCookie(values);
  };

  const getAndStoreEmulatorToken = async (values: { email: string; password: string }) => {
    const { user } = await signInWithEmailAndPassword(auth, values.email, values.password);
    await setTokenClient(await user.getIdToken(true));
  };

  const getAndStoreSessionCookie = async (values: { email: string; password: string }) => {
    const { user } = await signInWithEmailAndPassword(auth, values.email, values.password);
    const token = await user.getIdToken(true);
    await UserFactory.signIn({ token });
  }

  const getAndStoreFirebaseToken = async (values: { email: string; password: string }) => {
    const { idToken } = await UserFactory.getFirebaseToken({
      email: values.email,
      password: values.password,
    })
    await setTokenClient(idToken);
  }

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: LoginFormValidation,
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        setHasError(false);
        await signIn(values);

        if (flags.onboardingEnabled) {
          router.replace(`/onboarding`);
        } else {
          router.replace(`/treatments`);
        }
      } catch (error) {
        await signOut(auth);
        if (error instanceof FirebaseError && error.code === 'auth/invalid-credential') {
          formik.setFieldTouched('password', true, false);
          formik.setFieldTouched('email', true, false);
          formik.setFieldError('email', ' ');
          formik.setFieldError('password', translation('incorrect_user_password'));
          setHasError(true);
          setIsLoading(false);
          return;
        }

        if (error instanceof HttpResponseError && error.body.error_code === 'USER_NOT_FOUND') {
          setErrorType('USER_NOT_FOUND');
          setIsLoading(false);
          return;
        }

        setIsSuccess(false);
        setIsLoading(false);
      }
    },
  });

  const {
    resetForm,
    setFieldTouched,
    handleSubmit,
    handleChange,
    handleBlur,
    touched: formTouched,
    errors: formErrors,
    isValid: formIsValid,
    dirty: formIsDirty,
  } = formik;

  const handleResetForm = () => {
    resetForm();
    setIsSuccess(null);
    setIsLoading(false);
    setHasError(false);
  };

  const handleFocus = () => {
    if (!hasError) return;

    setFieldTouched('email', false, true);
    setFieldTouched('password', false, true);
    setHasError(false);
  };

  return {
    isSuccess,
    isLoading,
    formTouched,
    formErrors,
    errorType,
    formIsValid,
    formIsDirty,
    setIsSuccess,
    handleResetForm,
    setHasError,
    handleFocus,
    handleSubmit,
    handleChange,
    handleBlur,
  };
};

export { useLoginFormViewModel };
